import { useState, useRef, useEffect } from "react";
import { SelectOption } from "./Select";

interface FilterableSelectProps {
  options: SelectOption[];
  label: string;
  name: string;
  placeholder: string;
  value?: string;
  errors?: string;
  onChange?: (value: string) => void;
}

const FilterableSelect: React.FC<FilterableSelectProps> = ({
  options,
  label,
  name,
  placeholder,
  value,
  errors,
  onChange,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || "");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const highlightedRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (value !== undefined) setSelectedValue(value);
  }, [value]);

  const selectedLabel =
    options.find((o) => o.value === selectedValue)?.label || "";

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(query.toLowerCase()),
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  useEffect(() => {
    highlightedRef.current?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const handleSelect = (option: SelectOption) => {
    setSelectedValue(option.value);
    onChange?.(option.value);
    setQuery("");
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedValue("");
    onChange?.("");
    setQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filteredOptions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((i) => (i + 1) % filteredOptions.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(
          (i) => (i - 1 + filteredOptions.length) % filteredOptions.length,
        );
        break;
      case "Enter":
        e.preventDefault();
        handleSelect(filteredOptions[highlightedIndex]);
        break;
      case "Escape":
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  const showDropdown = isOpen && filteredOptions.length > 0;

  return (
    <div className="flex w-full flex-col gap-2">
      {label && (
        <label className="text-cream-800 dark:text-cream-400 text-sm font-medium">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? query : selectedLabel}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            setQuery("");
            setIsOpen(true);
          }}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`border-cream-500 placeholder-cream-600 selection:bg-peach-300 dark:placeholder-cream-500 dark:bg-cream-800 w-full rounded border font-medium ${
            selectedValue
              ? "text-cream-950 dark:text-cream-100"
              : "text-cream-600 dark:text-cream-500"
          }`}
          autoComplete="off"
        />
        {selectedValue && !isOpen && (
          <button
            type="button"
            onClick={handleClear}
            className="text-cream-500 hover:text-cream-700 dark:hover:text-cream-300 absolute top-1/2 right-3 -translate-y-1/2"
          >
            &times;
          </button>
        )}
        {showDropdown && (
          <ul className="border-cream-300 dark:border-cream-700 dark:bg-cream-900 absolute top-full left-0 z-10 mt-1 max-h-60 w-full overflow-auto rounded border bg-white shadow-lg">
            {filteredOptions.map((option, index) => (
              <li
                key={option.value}
                ref={index === highlightedIndex ? highlightedRef : null}
                onMouseDown={() => handleSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`cursor-pointer px-3 py-2 ${
                  index === highlightedIndex
                    ? "bg-peach-100 dark:bg-cream-800"
                    : "hover:bg-cream-50 dark:hover:bg-cream-800"
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      <input type="hidden" name={name} value={selectedValue} />
      {errors && (
        <span className="text-peach-600 dark:text-peach-400 text-sm font-semibold">
          {errors}
        </span>
      )}
    </div>
  );
};

export default FilterableSelect;
